import { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Loader2, LogOut, Star, Check, X, FileText, Copy, ExternalLink, Plus, Trash2, Inbox, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, SERVICE_LABELS } from "@/lib/api";
import { useNoindex } from "./AdminLogin";
import AdminAnalytics from "./AdminAnalytics";
import AdminBookings from "./AdminBookings";
import AdminWebsites from "./AdminWebsites";
import AdminPush from "./AdminPush";
import AdminAiManager from "./AdminAiManager";

const INQUIRY_STATUS = [
  { value: "neu", label: "Neu", cls: "text-goldlight border-goldlight/40 bg-goldlight/5" },
  { value: "in_bearbeitung", label: "In Bearbeitung", cls: "text-sky-300 border-sky-300/40 bg-sky-300/5" },
  { value: "erledigt", label: "Erledigt", cls: "text-zinc-500 border-zinc-500/40 bg-zinc-500/5" },
  { value: "abgelehnt", label: "Abgelehnt", cls: "text-red-400 border-red-400/40 bg-red-400/5" },
];

const REVIEW_STATUS = [
  { value: "neu", label: "Wartet", cls: "text-goldlight border-goldlight/40 bg-goldlight/5" },
  { value: "freigegeben", label: "Öffentlich", cls: "text-emerald-300 border-emerald-300/40 bg-emerald-300/5" },
  { value: "abgelehnt", label: "Abgelehnt", cls: "text-zinc-500 border-zinc-500/40 bg-zinc-500/5" },
];

const CONTRACT_STATUS = [
  { value: "gesendet", label: "Angebot", cls: "text-sky-300 border-sky-300/40 bg-sky-300/5" },
  { value: "unterschrieben", label: "Bestätigt", cls: "text-emerald-300 border-emerald-300/40 bg-emerald-300/5" },
  { value: "erledigt", label: "Erledigt", cls: "text-zinc-500 border-zinc-500/40 bg-zinc-500/5" },
];

const INVOICE_STATUS = [
  { value: "offen", label: "Offen", cls: "text-yellow-300 border-yellow-300/40 bg-yellow-300/5" },
  { value: "ueberfaellig", label: "Überfällig", cls: "text-red-400 border-red-400/40 bg-red-400/5" },
  { value: "bezahlt", label: "Bezahlt", cls: "text-emerald-300 border-emerald-300/40 bg-emerald-300/5" },
  { value: "erstattet", label: "Erstattet", cls: "text-sky-300 border-sky-300/40 bg-sky-300/5" },
];

const fmtEur = (cents) =>
  (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });

function Toggle({ label, checked, onChange, testId }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testId} className="flex items-center gap-3">
      <span className={`w-11 h-6 rounded-full relative transition-colors duration-300 ${checked ? "bg-goldlight" : "bg-zinc-700"}`}>
        <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all duration-300 ${checked ? "left-6" : "left-1"}`} />
      </span>
      <span className="text-sm text-zinc-300">{label}</span>
    </button>
  );
}

function Badge({ value, options }) {
  const s = options.find((x) => x.value === value) || options[0];
  return (
    <span className={`inline-block border px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] ${s.cls}`}>
      {s.label}
    </span>
  );
}

function Stars({ count }) {
  return (
    <span className="inline-flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={13} className={i < count ? "text-goldlight fill-goldlight" : "text-zinc-600"} />
      ))}
    </span>
  );
}

function ActionButton({ onClick, href, icon: Icon, children, testId, variant = "default" }) {
  const cls = `inline-flex items-center justify-center gap-2 border px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
    variant === "primary"
      ? "border-goldlight/60 text-goldlight hover:bg-goldlight/10"
      : variant === "success"
      ? "border-emerald-300/40 text-emerald-300 hover:bg-emerald-300/10"
      : variant === "danger"
      ? "border-white/15 text-zinc-500 hover:border-red-400 hover:text-red-400"
      : "border-white/15 text-zinc-400 hover:border-goldlight hover:text-goldlight"
  }`;
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId} className={cls}>
        {Icon && <Icon size={14} />} {children}
      </a>
    );
  }
  return (
    <button onClick={onClick} data-testid={testId} className={cls}>
      {Icon && <Icon size={14} />} {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">{label}</p>
      <div className="text-sm text-zinc-300">{children}</div>
    </div>
  );
}

function AiPanel({ q, onRetry }) {
  const [open, setOpen] = useState(false);

  if (q.ai_status === "pending") {
    return (
      <p className="text-xs text-zinc-500 flex items-center gap-2" data-testid={`inquiry-ai-pending-${q.id}`}>
        <Loader2 size={12} className="animate-spin" /> KI analysiert die Anfrage ...
      </p>
    );
  }
  if (q.ai_status === "failed") {
    return (
      <div className="flex items-center gap-3" data-testid={`inquiry-ai-failed-${q.id}`}>
        <p className="text-xs text-red-400">KI-Analyse fehlgeschlagen.</p>
        <button onClick={() => onRetry(q.id)} className="text-xs underline text-zinc-400 hover:text-goldlight">
          Erneut versuchen
        </button>
      </div>
    );
  }
  if (!q.ai) return null;

  const ai = q.ai;
  const hasPrice = ai.price_min != null && ai.price_max != null;

  return (
    <div className="border border-goldlight/20 bg-goldlight/[0.03] p-4" data-testid={`inquiry-ai-${q.id}`}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`inquiry-ai-toggle-${q.id}`}
        className="flex items-center justify-between w-full text-left"
      >
        <span className="text-[10px] uppercase tracking-[0.25em] text-goldlight">KI-Analyse · nur intern</span>
        <span className="text-xs text-zinc-500">{open ? "weniger" : "mehr"}</span>
      </button>
      {ai.summary && <p className="text-sm text-zinc-300 mt-2">{ai.summary}</p>}
      {hasPrice ? (
        <p className="mt-2 font-display text-goldlight text-xl" data-testid={`inquiry-ai-price-${q.id}`}>
          {ai.price_min}–{ai.price_max} €{" "}
          <span className="text-xs text-zinc-500 font-sans">geschätzt · Aufwand: {ai.effort || "unbekannt"}</span>
        </p>
      ) : (
        <p className="mt-2 text-sm text-zinc-400" data-testid={`inquiry-ai-price-${q.id}`}>
          Preis: weitere Informationen erforderlich
        </p>
      )}
      {open && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <Field label="Dienstleistung">{ai.service_guess || "—"}</Field>
          <Field label="Ort">{ai.location || "unbekannt"}</Field>
          <Field label="Anfahrt">{ai.distance_note || "—"}</Field>
          <Field label="Aufwand">{ai.effort || "—"}</Field>
          <Field label="Umfang">{ai.size || "—"}</Field>
          <Field label="Dringlichkeit">{ai.urgency || "—"}</Field>
          <Field label="Fotos vorhanden">{ai.photos_present ? "Ja" : "Nein"}</Field>
          <Field label="Fehlende Infos">
            {Array.isArray(ai.missing_info) && ai.missing_info.length ? ai.missing_info.join(", ") : "keine"}
          </Field>
          <div className="sm:col-span-2">
            <Field label="Begründung">{ai.price_note || "—"}</Field>
          </div>
          <p className="sm:col-span-2 text-zinc-500 italic text-[11px]">
            Unverbindliche interne Schätzung (Sicherheit: {ai.confidence || "unbekannt"}) — den endgültigen Preis legst du selbst fest.
          </p>
        </div>
      )}
    </div>
  );
}

const CONTRACT_INITIAL = {
  customer_name: "", customer_email: "", service: "garten",
  description: "", price: "", zeitraum: "", date: "",
};

const INVOICE_INITIAL = {
  customer_name: "", customer_email: "", customer_address: "", service: "garten",
  description: "", amount: "", due_days: "", contract_id: "",
};

function EmptyState({ icon: Icon, text, testId }) {
  return (
    <div className="flex flex-col items-center gap-4 text-zinc-500 py-20 border border-dashed border-white/10" data-testid={testId}>
      <Icon size={28} />
      <p className="text-sm uppercase tracking-[0.25em]">{text}</p>
    </div>
  );
}

function FormInput({ label, testId, textarea, ...props }) {
  return (
    <div className={props.className || ""}>
      <label className="block text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">{label}</label>
      {textarea ? (
        <textarea rows={4} data-testid={testId} {...props} className="field-input-dark resize-y w-full" />
      ) : (
        <input data-testid={testId} {...props} className="field-input-dark w-full" />
      )}
    </div>
  );
}

export default function AdminDashboard() {
  useNoindex("Admin | JUSTIN HILFT");
  const navigate = useNavigate();
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("anfragen");
  const [inquiries, setInquiries] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [contracts, setContracts] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [contractForm, setContractForm] = useState(CONTRACT_INITIAL);
  const [showContractForm, setShowContractForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState(INVOICE_INITIAL);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        try {
          await api.post("/auth/refresh");
          const { data } = await api.get("/auth/me");
          setUser(data);
        } catch {
          setUser(null);
        }
      }
    })();
  }, []);

  const loadInquiries = useCallback(async () => {
    const { data } = await api.get("/inquiries");
    setInquiries(data);
  }, []);

  const loadReviews = useCallback(async () => {
    const { data } = await api.get("/reviews/all");
    setReviews(data);
  }, []);

  const loadContracts = useCallback(async () => {
    const { data } = await api.get("/contracts");
    setContracts(data);
  }, []);

  const loadInvoices = useCallback(async () => {
    const [{ data: list }, { data: s }] = await Promise.all([
      api.get("/invoices"),
      api.get("/invoices/stats"),
    ]);
    setInvoices(list);
    setStats(s);
  }, []);

  const loadSettings = useCallback(async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
  }, []);

  useEffect(() => {
    if (!user) return;
    loadInquiries().catch(() => toast.error("Anfragen konnten nicht geladen werden."));
    loadReviews().catch(() => {});
    loadContracts().catch(() => {});
    loadInvoices().catch(() => {});
    loadSettings().catch(() => {});
  }, [user, loadInquiries, loadReviews, loadContracts, loadInvoices, loadSettings]);

  useEffect(() => {
    if (!inquiries?.some((q) => q.ai_status === "pending")) return;
    const t = setTimeout(() => loadInquiries().catch(() => {}), 5000);
    return () => clearTimeout(t);
  }, [inquiries, loadInquiries]);

  if (user === null) return <Navigate to="/admin/login" replace />;

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const setInquiryStatus = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}`, { status });
      setInquiries((l) => l.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success("Status aktualisiert.");
    } catch {
      toast.error("Status konnte nicht geändert werden.");
    }
  };

  const deleteInquiry = async (id) => {
    try {
      await api.delete(`/inquiries/${id}`);
      setInquiries((l) => l.filter((q) => q.id !== id));
      toast.success("Anfrage gelöscht.");
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const reanalyze = async (id) => {
    try {
      await api.post(`/inquiries/${id}/analyze`);
      setInquiries((l) => l.map((q) => (q.id === id ? { ...q, ai_status: "pending" } : q)));
      toast.success("KI-Analyse neu gestartet.");
    } catch {
      toast.error("Analyse konnte nicht gestartet werden.");
    }
  };

  const acceptInquiry = (q) => {
    const ai = q.ai;
    const priceSuggestion =
      ai && ai.price_min != null && ai.price_max != null ? `${ai.price_min} €` : "";
    setContractForm({
      customer_name: q.name,
      customer_email: q.email,
      service: q.service,
      description: q.message + (q.address ? `\n\nOrt: ${q.address}` : ""),
      price: priceSuggestion,
      zeitraum: q.zeitraum || "",
    });
    setShowContractForm(true);
    setTab("vertraege");
    if (q.status === "neu") setInquiryStatus(q.id, "in_bearbeitung");
    toast.success("Auftrag vorbereitet — Preis prüfen und absenden, der Rest läuft automatisch.");
  };

  const setReviewStatus = async (id, status) => {
    try {
      await api.patch(`/reviews/${id}`, { status });
      setReviews((l) => l.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(status === "freigegeben" ? "Bewertung ist jetzt öffentlich." : "Status aktualisiert.");
    } catch {
      toast.error("Status konnte nicht geändert werden.");
    }
  };

  const deleteReview = async (id) => {
    try {
      await api.delete(`/reviews/${id}`);
      setReviews((l) => l.filter((r) => r.id !== id));
      toast.success("Bewertung gelöscht.");
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const createContract = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/contracts", contractForm);
      toast.success(`Auftrag ${data.number} erstellt und per E-Mail versendet.`);
      setContractForm(CONTRACT_INITIAL);
      setShowContractForm(false);
      loadContracts();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const setContractStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/contracts/${id}`, { status });
      setContracts((l) => l.map((c) => (c.id === id ? { ...c, status } : c)));
      if (data.invoice_created) {
        toast.success(`Erledigt — Rechnung ${data.invoice_number} wurde automatisch erstellt und an den Kunden versendet.`);
        loadInvoices();
      } else {
        toast.success("Status aktualisiert.");
      }
    } catch {
      toast.error("Status konnte nicht geändert werden.");
    }
  };

  const saveContractNotes = async (id, notes) => {
    try {
      await api.patch(`/contracts/${id}`, { notes });
      toast.success("Notiz gespeichert.");
    } catch {
      toast.error("Notiz konnte nicht gespeichert werden.");
    }
  };

  const deleteContract = async (id) => {
    try {
      await api.delete(`/contracts/${id}`);
      setContracts((l) => l.filter((c) => c.id !== id));
      toast.success("Auftrag gelöscht.");
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const copyLink = (id) => {
    const link = `https://justinhilft.de/vertrag/${id}`;
    navigator.clipboard.writeText(link).then(() => toast.success("Link kopiert.")).catch(() => toast.info(link));
  };

  const createInvoice = async (e) => {
    e.preventDefault();
    setCreatingInvoice(true);
    try {
      const payload = { ...invoiceForm, due_days: invoiceForm.due_days ? Number(invoiceForm.due_days) : null };
      const { data } = await api.post("/invoices", payload);
      toast.success(`Rechnung ${data.number} erstellt und per E-Mail versendet.`);
      setInvoiceForm(INVOICE_INITIAL);
      setShowInvoiceForm(false);
      loadInvoices();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreatingInvoice(false);
    }
  };

  const prefillInvoice = (c) => {
    setInvoiceForm({
      customer_name: c.customer_name,
      customer_email: c.customer_email,
      customer_address: "",
      service: c.service,
      description: c.description,
      amount: c.price,
      due_days: "",
      contract_id: c.id,
    });
    setShowInvoiceForm(true);
    setTab("rechnungen");
  };

  const markInvoicePaid = async (id) => {
    try {
      await api.patch(`/invoices/${id}`, { status: "bezahlt", paid_method: "ueberweisung" });
      toast.success("Als bezahlt markiert.");
      loadInvoices();
    } catch {
      toast.error("Fehlgeschlagen.");
    }
  };

  const sendInvoiceMail = async (id) => {
    try {
      await api.post(`/invoices/${id}/send`);
      toast.success("Rechnung erneut versendet.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendReminderMail = async (id) => {
    try {
      await api.post(`/invoices/${id}/remind`);
      toast.success("Erinnerung versendet.");
      loadInvoices();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteInvoice = async (id) => {
    try {
      await api.delete(`/invoices/${id}`);
      setInvoices((l) => l.filter((i) => i.id !== id));
      toast.success("Rechnung gelöscht.");
      loadInvoices();
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const refundInvoice = async (inv) => {
    if (!window.confirm(`Erstattung für ${inv.number} über ${fmtEur(inv.amount_cents)} wirklich auslösen?\n\nDas Geld geht automatisch an den Kunden zurück. Das kann nicht rückgängig gemacht werden.`)) return;
    try {
      await api.post(`/invoices/${inv.id}/refund`);
      toast.success("Erstattung ausgelöst — der Kunde wurde per E-Mail informiert.");
      loadInvoices();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyInvoiceLink = (id) => {
    const link = `https://justinhilft.de/rechnung/${id}`;
    navigator.clipboard.writeText(link).then(() => toast.success("Rechnungs-Link kopiert.")).catch(() => toast.info(link));
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/settings", settings);
      setSettings(data);
      toast.success("Einstellungen gespeichert.");
    } catch {
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSavingSettings(false);
    }
  };

  const counts = (inquiries || []).reduce(
    (acc, q) => ({ ...acc, [q.status]: (acc[q.status] || 0) + 1 }),
    {}
  );
  const newReviews = (reviews || []).filter((r) => r.status === "neu").length;

  const TABS = [
    { id: "anfragen", label: "Anfragen", count: counts.neu || 0 },
    { id: "buchungen", label: "Buchungen", count: 0 },
    { id: "websites", label: "Websites", count: 0 },
    { id: "ki-manager", label: "KI-Manager", count: 0 },
    { id: "bewertungen", label: "Bewertungen", count: newReviews },
    { id: "vertraege", label: "Verträge", count: 0 },
    { id: "rechnungen", label: "Rechnungen", count: stats?.overdue_count || 0 },
    { id: "analytics", label: "Analytics", count: 0 },
    { id: "einstellungen", label: "Einstellungen", count: 0 },
  ];

  const loadingRow = (
    <div className="flex items-center gap-3 text-zinc-500 py-20 justify-center">
      <Loader2 size={18} className="animate-spin" /> Lade ...
    </div>
  );

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-coal text-bone px-4 sm:px-6 lg:px-12 py-6 lg:py-10">
      <div className="max-w-[1100px] mx-auto">
        <header className="flex flex-col gap-5 border-b border-white/10 pb-6 mb-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-display font-bold uppercase tracking-tight text-lg">
                Justin Hilft<span className="text-goldlight">.</span>
              </p>
              <p data-testid="admin-welcome" className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mt-1">
                Admin {user ? `· ${user.email}` : ""}
              </p>
            </div>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] hover:border-goldlight hover:text-goldlight transition-colors duration-300"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
          <Link to="/" data-testid="admin-back-to-site" className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 hover:text-goldlight transition-colors duration-300">
            ← Zur Website
          </Link>
        </header>

        <nav className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`whitespace-nowrap px-5 py-3 text-[11px] uppercase tracking-[0.2em] border transition-colors duration-300 ${
                tab === t.id
                  ? "border-goldlight text-goldlight"
                  : "border-white/10 text-zinc-500 hover:text-bone"
              }`}
            >
              {t.label}
              {t.count > 0 && <span className="ml-2 text-goldlight">({t.count})</span>}
            </button>
          ))}
        </nav>

        {user === undefined ? (
          loadingRow
        ) : tab === "buchungen" ? (
          <AdminBookings />
        ) : tab === "websites" ? (
          <AdminWebsites />
        ) : tab === "ki-manager" ? (
          <AdminAiManager />
        ) : tab === "analytics" ? (
          <AdminAnalytics />
        ) : tab === "anfragen" ? (
          inquiries === null ? (
            loadingRow
          ) : inquiries.length === 0 ? (
            <EmptyState icon={Inbox} text="Noch keine Anfragen" testId="admin-empty-state" />
          ) : (
            <div className="space-y-5" data-testid="admin-inquiries-table">
              {inquiries.map((q) => (
                <article key={q.id} data-testid={`inquiry-row-${q.id}`} className="border border-white/10 p-5 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-zinc-500">{fmtDate(q.created_at)}</span>
                    <Badge value={q.status} options={INQUIRY_STATUS} />
                  </div>
                  <div>
                    <p className="font-medium text-base">{q.name}</p>
                    <a href={`mailto:${q.email}`} className="text-sm text-zinc-500 hover:text-goldlight transition-colors duration-300">{q.email}</a>
                    {q.phone && <p className="text-sm text-zinc-500">{q.phone}</p>}
                    {q.address && <p className="text-sm text-zinc-500">{q.address}</p>}
                  </div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-goldlight/80">
                    {SERVICE_LABELS[q.service] || q.service}
                  </p>
                  <p className="text-sm text-zinc-400 leading-relaxed whitespace-pre-wrap">{q.message}</p>
                  {(q.zeitraum || q.budget) && (
                    <div className="text-xs text-zinc-500 space-y-1">
                      {q.zeitraum && <p>Zeitraum: {q.zeitraum}</p>}
                      {q.budget && <p>Budget/Infos: {q.budget}</p>}
                    </div>
                  )}
                  {q.photos?.length > 0 && (
                    <div className="flex flex-wrap gap-3" data-testid={`inquiry-photos-${q.id}`}>
                      {q.photos.map((p) => (
                        <a key={p} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} target="_blank" rel="noopener noreferrer">
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`}
                            alt="Kundenfoto"
                            className="w-20 h-20 object-cover border border-white/10 hover:border-goldlight transition-colors duration-300"
                          />
                        </a>
                      ))}
                    </div>
                  )}
                  <AiPanel q={q} onRetry={reanalyze} />
                  <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-white/5">
                    <select
                      value={q.status}
                      onChange={(e) => setInquiryStatus(q.id, e.target.value)}
                      data-testid={`inquiry-status-select-${q.id}`}
                      className="bg-surface border border-white/10 text-xs px-3 py-2.5 text-zinc-300 cursor-pointer"
                    >
                      {INQUIRY_STATUS.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                    <ActionButton onClick={() => acceptInquiry(q)} icon={Check} variant="success" testId={`inquiry-accept-${q.id}`}>
                      Annehmen
                    </ActionButton>
                    <ActionButton onClick={() => setInquiryStatus(q.id, "abgelehnt")} icon={X} testId={`inquiry-reject-${q.id}`}>
                      Ablehnen
                    </ActionButton>
                    <ActionButton onClick={() => deleteInquiry(q.id)} icon={Trash2} variant="danger" testId={`inquiry-delete-${q.id}`}>
                      Löschen
                    </ActionButton>
                  </div>
                </article>
              ))}
            </div>
          )
        ) : tab === "bewertungen" ? (
          reviews === null ? (
            loadingRow
          ) : reviews.length === 0 ? (
            <EmptyState icon={Star} text="Noch keine Bewertungen" testId="reviews-empty-admin" />
          ) : (
            <div className="space-y-5" data-testid="admin-reviews-list">
              {reviews.map((r) => (
                <article key={r.id} data-testid={`review-row-${r.id}`} className="border border-white/10 p-5 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <Stars count={r.stars} />
                    <Badge value={r.status} options={REVIEW_STATUS} />
                  </div>
                  <div>
                    <p className="font-medium">{r.name}</p>
                    <p className="text-xs text-zinc-500 mt-1">
                      {r.service ? SERVICE_LABELS[r.service] || r.service : "Allgemein"} · {new Date(r.created_at).toLocaleDateString("de-DE")}
                    </p>
                  </div>
                  <p className="text-sm text-zinc-400 leading-relaxed">„{r.text}"</p>
                  <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-white/5">
                    {r.status !== "freigegeben" && (
                      <ActionButton onClick={() => setReviewStatus(r.id, "freigegeben")} icon={Check} variant="success" testId={`review-approve-${r.id}`}>
                        Freigeben
                      </ActionButton>
                    )}
                    {r.status !== "abgelehnt" && (
                      <ActionButton onClick={() => setReviewStatus(r.id, "abgelehnt")} icon={X} testId={`review-reject-${r.id}`}>
                        Ablehnen
                      </ActionButton>
                    )}
                    <ActionButton onClick={() => deleteReview(r.id)} icon={Trash2} variant="danger" testId={`review-delete-${r.id}`}>
                      Löschen
                    </ActionButton>
                  </div>
                </article>
              ))}
            </div>
          )
        ) : tab === "vertraege" ? (
          <div className="space-y-5">
            {!showContractForm ? (
              <ActionButton onClick={() => setShowContractForm(true)} icon={Plus} variant="primary" testId="contract-open-form-button">
                Neuer Auftrag
              </ActionButton>
            ) : (
              <form onSubmit={createContract} data-testid="contract-form" className="border border-white/10 bg-surface p-6 space-y-5">
                <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500">Neue Auftragsbestätigung</p>
                <FormInput label="Kundenname *" required testId="contract-name-input" value={contractForm.customer_name} onChange={(e) => setContractForm((f) => ({ ...f, customer_name: e.target.value }))} placeholder="Max Mustermann" />
                <FormInput label="Kunden-E-Mail *" required type="email" testId="contract-email-input" value={contractForm.customer_email} onChange={(e) => setContractForm((f) => ({ ...f, customer_email: e.target.value }))} placeholder="kunde@beispiel.de" />
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Dienstleistung *</label>
                  <select data-testid="contract-service-select" value={contractForm.service} onChange={(e) => setContractForm((f) => ({ ...f, service: e.target.value }))} className="field-input-dark appearance-none cursor-pointer w-full">
                    {Object.entries(SERVICE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <FormInput label="Preis *" required testId="contract-price-input" value={contractForm.price} onChange={(e) => setContractForm((f) => ({ ...f, price: e.target.value }))} placeholder="z. B. 150 €" />
                <FormInput label="Zeitraum" testId="contract-zeitraum-input" value={contractForm.zeitraum} onChange={(e) => setContractForm((f) => ({ ...f, zeitraum: e.target.value }))} placeholder="z. B. Sa, 15.08.2026" />
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Fester Termin (optional)</label>
                  <input
                    type="date"
                    data-testid="contract-date-input"
                    value={contractForm.date || ""}
                    onChange={(e) => setContractForm((f) => ({ ...f, date: e.target.value }))}
                    className="field-input-dark w-full [color-scheme:dark]"
                  />
                  <p className="text-[11px] text-zinc-600 mt-1.5">Blockiert nach der Unterschrift automatisch diesen Tag im Tierbetreuung-Kalender.</p>
                </div>
                <FormInput label="Leistungsbeschreibung *" required textarea testId="contract-description-input" value={contractForm.description} onChange={(e) => setContractForm((f) => ({ ...f, description: e.target.value }))} placeholder="Was wird gemacht, wann, wo ..." />
                <div className="flex flex-wrap gap-3">
                  <button type="submit" disabled={creating} data-testid="contract-submit-button" className="inline-flex items-center gap-2 bg-goldlight text-coal px-6 py-3.5 text-[11px] uppercase tracking-[0.25em] disabled:opacity-50">
                    {creating && <Loader2 size={14} className="animate-spin" />}
                    {creating ? "Wird versendet ..." : "Erstellen & per E-Mail senden"}
                  </button>
                  <ActionButton onClick={() => setShowContractForm(false)}>Abbrechen</ActionButton>
                </div>
              </form>
            )}

            {contracts === null ? (
              loadingRow
            ) : contracts.length === 0 ? (
              <EmptyState icon={FileText} text="Noch keine Aufträge" testId="contracts-empty-admin" />
            ) : (
              contracts.map((c) => (
                <article key={c.id} data-testid={`contract-row-${c.id}`} className="border border-white/10 p-5 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-display tracking-tight text-lg">{c.number}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">{new Date(c.created_at).toLocaleDateString("de-DE")}</p>
                    </div>
                    <Badge value={c.status} options={CONTRACT_STATUS} />
                  </div>
                  <div>
                    <p className="font-medium">{c.customer_name}</p>
                    <p className="text-sm text-zinc-500">{c.customer_email}</p>
                    <p className="text-sm text-zinc-400 mt-1">{SERVICE_LABELS[c.service] || c.service} · <span className="text-bone">{c.price}</span></p>
                  </div>
                  <input
                    defaultValue={c.notes || ""}
                    onBlur={(e) => saveContractNotes(c.id, e.target.value)}
                    data-testid={`contract-notes-${c.id}`}
                    placeholder="Interne Notiz ..."
                    className="w-full bg-transparent border-0 border-b border-white/10 text-sm text-zinc-400 placeholder:text-zinc-600 focus:outline-none focus:border-goldlight py-2"
                  />
                  <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-white/5">
                    <select
                      value={c.status}
                      onChange={(e) => setContractStatus(c.id, e.target.value)}
                      data-testid={`contract-status-${c.id}`}
                      className="bg-surface border border-white/10 text-xs px-3 py-2.5 text-zinc-300 cursor-pointer"
                    >
                      {CONTRACT_STATUS.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                    {!c.invoice_id && (
                      <ActionButton onClick={() => prefillInvoice(c)} icon={FileText} variant="primary" testId={`contract-invoice-${c.id}`}>
                        Rechnung
                      </ActionButton>
                    )}
                    <ActionButton onClick={() => copyLink(c.id)} icon={Copy} testId={`contract-copy-${c.id}`}>
                      Link
                    </ActionButton>
                    <ActionButton href={`${process.env.REACT_APP_BACKEND_URL}/api/contracts/${c.id}/pdf`} icon={ExternalLink} testId={`contract-pdf-${c.id}`}>
                      PDF
                    </ActionButton>
                    <ActionButton onClick={() => deleteContract(c.id)} icon={Trash2} variant="danger" testId={`contract-delete-${c.id}`}>
                      Löschen
                    </ActionButton>
                  </div>
                </article>
              ))
            )}
          </div>
        ) : tab === "rechnungen" ? (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="invoice-stats">
              <div className="border border-yellow-300/20 bg-yellow-300/[0.03] px-5 py-5" data-testid="invoice-stat-open">
                <p className="text-[10px] uppercase tracking-[0.25em] text-yellow-300 mb-2">Offen</p>
                <p className="font-display text-2xl">{stats ? fmtEur(stats.open_sum) : "—"}</p>
                <p className="text-xs text-zinc-500 mt-1">{stats?.open_count || 0} Rechnung(en)</p>
              </div>
              <div className="border border-red-400/20 bg-red-400/[0.03] px-5 py-5" data-testid="invoice-stat-overdue">
                <p className="text-[10px] uppercase tracking-[0.25em] text-red-400 mb-2">Überfällig</p>
                <p className="font-display text-2xl">{stats ? fmtEur(stats.overdue_sum) : "—"}</p>
                <p className="text-xs text-zinc-500 mt-1">{stats?.overdue_count || 0} Rechnung(en)</p>
              </div>
              <div className="border border-emerald-300/20 bg-emerald-300/[0.03] px-5 py-5" data-testid="invoice-stat-paid">
                <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-300 mb-2">Bezahlt · Umsatz</p>
                <p className="font-display text-2xl">{stats ? fmtEur(stats.paid_sum) : "—"}</p>
                <p className="text-xs text-zinc-500 mt-1">{stats?.paid_count || 0} Rechnung(en)</p>
              </div>
            </div>

            {!showInvoiceForm ? (
              <ActionButton onClick={() => setShowInvoiceForm(true)} icon={Plus} variant="primary" testId="invoice-open-form-button">
                Neue Rechnung
              </ActionButton>
            ) : (
              <form onSubmit={createInvoice} data-testid="invoice-form" className="border border-white/10 bg-surface p-6 space-y-5">
                <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500">Neue Rechnung</p>
                <FormInput label="Kundenname *" required testId="invoice-name-input" value={invoiceForm.customer_name} onChange={(e) => setInvoiceForm((f) => ({ ...f, customer_name: e.target.value }))} placeholder="Max Mustermann" />
                <FormInput label="Kunden-E-Mail *" required type="email" testId="invoice-email-input" value={invoiceForm.customer_email} onChange={(e) => setInvoiceForm((f) => ({ ...f, customer_email: e.target.value }))} placeholder="kunde@beispiel.de" />
                <FormInput label="Adresse" testId="invoice-address-input" value={invoiceForm.customer_address} onChange={(e) => setInvoiceForm((f) => ({ ...f, customer_address: e.target.value }))} placeholder="Straße, PLZ Ort" />
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Dienstleistung *</label>
                  <select data-testid="invoice-service-select" value={invoiceForm.service} onChange={(e) => setInvoiceForm((f) => ({ ...f, service: e.target.value }))} className="field-input-dark appearance-none cursor-pointer w-full">
                    {Object.entries(SERVICE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <FormInput label="Betrag *" required testId="invoice-amount-input" value={invoiceForm.amount} onChange={(e) => setInvoiceForm((f) => ({ ...f, amount: e.target.value }))} placeholder="z. B. 150 €" />
                <FormInput label={`Zahlungsziel in Tagen (Standard: ${settings?.payment_term_days || 7})`} type="number" min="1" testId="invoice-due-input" value={invoiceForm.due_days} onChange={(e) => setInvoiceForm((f) => ({ ...f, due_days: e.target.value }))} placeholder={String(settings?.payment_term_days || 7)} />
                <FormInput label="Leistungsbeschreibung *" required textarea testId="invoice-description-input" value={invoiceForm.description} onChange={(e) => setInvoiceForm((f) => ({ ...f, description: e.target.value }))} placeholder="Was wurde gemacht ..." />
                <div className="flex flex-wrap gap-3">
                  <button type="submit" disabled={creatingInvoice} data-testid="invoice-submit-button" className="inline-flex items-center gap-2 bg-goldlight text-coal px-6 py-3.5 text-[11px] uppercase tracking-[0.25em] disabled:opacity-50">
                    {creatingInvoice && <Loader2 size={14} className="animate-spin" />}
                    {creatingInvoice ? "Wird versendet ..." : "Erstellen & per E-Mail senden"}
                  </button>
                  <ActionButton onClick={() => setShowInvoiceForm(false)}>Abbrechen</ActionButton>
                </div>
              </form>
            )}

            {invoices === null ? (
              loadingRow
            ) : invoices.length === 0 ? (
              <EmptyState icon={FileText} text="Noch keine Rechnungen" testId="invoices-empty-admin" />
            ) : (
              invoices.map((inv) => (
                <article key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border border-white/10 p-5 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-display tracking-tight text-lg">{inv.number}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">Fällig: {inv.due_at.slice(0, 10)}</p>
                    </div>
                    <Badge value={inv.status} options={INVOICE_STATUS} />
                  </div>
                  <div>
                    <p className="font-medium">{inv.customer_name}</p>
                    <p className="text-sm text-zinc-500">{inv.customer_email}</p>
                  </div>
                  <p className="font-display text-2xl text-goldlight">{fmtEur(inv.amount_cents)}</p>
                  <p className="text-sm text-zinc-400">
                    {SERVICE_LABELS[inv.service] || inv.service}
                    {inv.paid_method ? ` · bezahlt per ${inv.paid_method === "stripe" ? "Online-Zahlung" : "Überweisung"}` : ""}
                    {inv.reminders_sent?.length > 0 ? ` · ${inv.reminders_sent.length} Erinnerung(en) versendet` : ""}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-white/5">
                    {inv.status !== "bezahlt" && (
                      <>
                        <ActionButton onClick={() => markInvoicePaid(inv.id)} icon={Check} variant="success" testId={`invoice-paid-${inv.id}`}>
                          Bezahlt
                        </ActionButton>
                        <ActionButton onClick={() => sendReminderMail(inv.id)} testId={`invoice-remind-${inv.id}`}>
                          Erinnerung
                        </ActionButton>
                      </>
                    )}
                    <ActionButton onClick={() => sendInvoiceMail(inv.id)} testId={`invoice-send-${inv.id}`}>
                      E-Mail
                    </ActionButton>
                    <ActionButton onClick={() => copyInvoiceLink(inv.id)} icon={Copy} testId={`invoice-copy-${inv.id}`}>
                      Link
                    </ActionButton>
                    <ActionButton href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.id}/pdf`} icon={ExternalLink} testId={`invoice-pdf-${inv.id}`}>
                      PDF
                    </ActionButton>
                    {inv.status === "bezahlt" && inv.paid_method === "stripe" && (
                      <ActionButton onClick={() => refundInvoice(inv)} icon={Undo2} testId={`invoice-refund-${inv.id}`}>
                        Erstatten
                      </ActionButton>
                    )}
                    <ActionButton onClick={() => deleteInvoice(inv.id)} icon={Trash2} variant="danger" testId={`invoice-delete-${inv.id}`}>
                      Löschen
                    </ActionButton>
                  </div>
                </article>
              ))
            )}
          </div>
        ) : settings === null ? (
          loadingRow
        ) : (
          <div className="space-y-10 max-w-xl" data-testid="settings-form">
            <div data-testid="settings-push-section">
              <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-5">Benachrichtigungen aufs iPhone</p>
              <AdminPush />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-5">Zahlungsziel</p>
              <FormInput
                label="Standard-Zahlungsfrist (Tage)"
                type="number"
                min="1"
                testId="settings-payment-term"
                value={settings.payment_term_days}
                onChange={(e) => setSettings((s) => ({ ...s, payment_term_days: Number(e.target.value) }))}
              />
            </div>

            <div className="space-y-6">
              <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500">Automatische Erinnerungen</p>
              <Toggle
                label="Freundliche Erinnerung vor und nach Fälligkeit"
                checked={settings.reminder_enabled}
                onChange={(v) => setSettings((s) => ({ ...s, reminder_enabled: v }))}
                testId="settings-reminder-toggle"
              />
              {settings.reminder_enabled && (
                <div className="grid grid-cols-2 gap-4">
                  <FormInput label="Tage vor Fälligkeit" type="number" min="0" testId="settings-reminder-before" value={settings.reminder_before_days} onChange={(e) => setSettings((s) => ({ ...s, reminder_before_days: Number(e.target.value) }))} />
                  <FormInput label="Tage nach Fälligkeit" type="number" min="0" testId="settings-reminder-after" value={settings.reminder_after_days} onChange={(e) => setSettings((s) => ({ ...s, reminder_after_days: Number(e.target.value) }))} />
                </div>
              )}
              <Toggle
                label="Mahnung aktivieren (erst nach deiner Freigabe)"
                checked={settings.mahnung_enabled}
                onChange={(v) => setSettings((s) => ({ ...s, mahnung_enabled: v }))}
                testId="settings-mahnung-toggle"
              />
              {settings.mahnung_enabled && (
                <FormInput label="Mahnung nach X Tagen überfällig" type="number" min="1" testId="settings-mahnung-after" value={settings.mahnung_after_days} onChange={(e) => setSettings((s) => ({ ...s, mahnung_after_days: Number(e.target.value) }))} />
              )}
              <p className="text-xs text-zinc-500 italic">
                Überfällige Rechnungen werden automatisch markiert. Es werden keine Mahngebühren erhoben.
              </p>
            </div>

            <div className="space-y-6">
              <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500">
                E-Mail-Texte (Platzhalter: {"{name}"}, {"{number}"}, {"{amount}"}, {"{service}"}, {"{due_date}"}, {"{link}"})
              </p>
              {[
                ["tpl_invoice", "Rechnung", "settings-tpl-invoice"],
                ["tpl_reminder", "Zahlungserinnerung", "settings-tpl-reminder"],
                ["tpl_mahnung", "Mahnung", "settings-tpl-mahnung"],
                ["tpl_payment_confirm", "Zahlungsbestätigung", "settings-tpl-confirm"],
              ].map(([key, label, testId]) => (
                <FormInput
                  key={key}
                  label={label}
                  textarea
                  testId={testId}
                  value={settings[key]}
                  onChange={(e) => setSettings((s) => ({ ...s, [key]: e.target.value }))}
                />
              ))}
            </div>

            <div className="border border-goldlight/30 bg-goldlight/5 p-5 space-y-3">
              <p className="text-[11px] uppercase tracking-[0.3em] text-goldlight">Stripe Webhook-Secret (Experten)</p>
              <p className="text-xs text-zinc-500 leading-relaxed">
                Nur ändern, wenn Stripe-Zahlungen nicht als „bezahlt" markiert werden. Den aktuellen Wert findest du im
                Stripe-Dashboard → Entwickler → Webhooks → justinhilft.de → Signatur-Schlüssel.
              </p>
              <FormInput
                label="Webhook-Secret (whsec_...)"
                testId="settings-webhook-secret"
                value={settings.stripe_webhook_secret || ""}
                onChange={(e) => setSettings((s) => ({ ...s, stripe_webhook_secret: e.target.value }))}
                placeholder="Leer = System-Wert verwenden"
              />
            </div>

            <button
              onClick={saveSettings}
              disabled={savingSettings}
              data-testid="settings-save-button"
              className="inline-flex items-center gap-2 bg-goldlight text-coal px-8 py-3.5 text-[11px] uppercase tracking-[0.25em] disabled:opacity-50 w-full sm:w-auto justify-center"
            >
              {savingSettings && <Loader2 size={14} className="animate-spin" />}
              {savingSettings ? "Speichere ..." : "Einstellungen speichern"}
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
